import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import GlassButton from '../ui/GlassButton';
import SwapWidget from '../ui/SwapWidget';
import { heroLinks } from '../../constants/content';
import { track } from '../../lib/analytics';
import { useSectionView } from '../../hooks/useSectionView';

export default function Hero() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const sectionRef = useSectionView('hero', 0);

  return (
    <section
      ref={sectionRef}
      id="home"
      className={`min-h-screen flex items-center justify-center relative overflow-hidden ${
        darkMode ? 'bg-main-black' : 'bg-gradient-to-b from-gray-50 to-white'
      }`}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl opacity-20 ${
          darkMode ? 'bg-main' : 'bg-main/30'
        }`} />
        <div className={`absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl opacity-20 ${
          darkMode ? 'bg-main-orange' : 'bg-main-orange/30'
        }`} />
      </div>

      <div className="mainCont relative z-10 py-20">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          {/* Left column: Text content */}
          <div className="flex-1 text-center lg:text-left">
            {/* Logo */}
            <div className="mb-8">
              <img
                src={darkMode ? '/images/logo-dark.svg' : '/images/logo-light.svg'}
                alt="Soroswap"
                className="h-20 md:h-32 lg:h-[250px] mx-auto lg:mx-0"
              />
            </div>

            {/* Pre-headline */}
            <span className="text-main uppercase tracking-widest text-sm font-semibold mb-6 block">
              {t('hero.preHeadline')}
            </span>

            {/* Main headline */}
            <h1 className={`titleFont text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {t('hero.headline')}
            </h1>

            {/* Subheadline */}
            <p className={`text-lg md:text-xl max-w-3xl mx-auto lg:mx-0 mb-10 ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t('hero.subheadline')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
              <GlassButton
                variant="primary"
                size="lg"
                href={heroLinks.primaryCTA}
                onClick={() => {
                  track('cta_clicked', {
                    cta_id: 'api_access',
                    cta_text: t('hero.primaryCTA'),
                    section: 'hero',
                    destination_url: heroLinks.primaryCTA,
                    destination_type: 'app'
                  });
                }}
              >
                {t('hero.primaryCTA')}
              </GlassButton>

              <GlassButton
                variant="ghost"
                size="lg"
                href={heroLinks.secondaryCTA}
                onClick={() => {
                  track('cta_clicked', {
                    cta_id: 'docs_hero',
                    cta_text: t('hero.secondaryCTA'),
                    section: 'hero',
                    destination_url: heroLinks.secondaryCTA,
                    destination_type: 'docs'
                  });
                }}
                icon={
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                }
              >
                {t('hero.secondaryCTA')}
              </GlassButton>
            </div>
          </div>

          {/* Swap Widget */}
          <div className="flex justify-center lg:justify-end flex-shrink-0">
            <SwapWidget />
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="flex justify-center mt-10">
          <button
            onClick={() => {
              document.getElementById('trusted-by')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="animate-bounce cursor-pointer hover:scale-110 transition-transform"
            aria-label={t('hero.scrollToNext')}
          >
            <svg
              className={`w-6 h-6 ${darkMode ? 'text-white/50 hover:text-white' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
